import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import JSONModel from "sap/ui/model/json/JSONModel";
import JSONListBinding from "sap/ui/model/json/JSONListBinding";
import Dialog from "sap/m/Dialog";
import Table from "sap/ui/table/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import { AggregationBindingInfo } from "sap/ui/base/ManagedObject";
import V4Context from "sap/ui/model/odata/v4/Context";
import { Field$ChangeEvent } from "sap/ui/mdc/Field";
import ValueHelp from "sap/ui/mdc/ValueHelp";
import SearchField, { SearchField$SearchEvent } from "sap/m/SearchField";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import MeasurementUnitService from "vamos/cockpit/data/measurementunit/service/MeasurementUnitService";
import formatter from "vamos/cockpit/base/formatter/formatter";
import NewArticleDialogController from "vamos/cockpit/data/article/controller/NewArticleDialog.controller";
import NewVariantDialogController from "vamos/cockpit/data/article/controller/NewVariantDialog.controller";
import { Article, ImportProblem, Product } from "vamos/cockpit/model/odata/VamosModel";

/**
 * @namespace vamos.cockpit.data.import.problem.controller
 */
export default class ImportProblemDetailsDialogController extends BaseTypeController {
    public formatter: formatter = formatter;

    private dialog: Dialog;
    private importProblemModel: JSONModel;
    private dialogModel: JSONModel;
    private importProblemVariantsTable: Table;
    private variantBinding: JSONListBinding;
    private measurementUnitService: MeasurementUnitService;
    private measurementLoadedPromise: Promise<V4Context[]>;
    private importProblemContexts: V4Context[];
    private saveCallBack: () => void;
    private hasSaved = false;
    private variantSearchQuery = "";

    public onInit(): void {
        super.onInit();
        this.dialog = this.getView().byId("importProblemDetailsDialog") as Dialog;
        this.dialog.attachAfterClose(this.onAfterClose);

        this.importProblemModel = new JSONModel();
        this.dialogModel = new JSONModel({ currentIndex: 0, total: 0 });
        this.setModel(this.importProblemModel, "ImportProblem");
        this.setModel(this.dialogModel, "importProblemDialog");

        this.measurementUnitService = new MeasurementUnitService(this.getComponent());
        this.measurementLoadedPromise = this.measurementUnitService.findPieceAndLengthUnits();

        (this.getModel() as ODataModel).bindList("/Products", undefined, undefined, undefined, { $count: true });

        this.importProblemVariantsTable = this.byId("importProblemsPossibleVariantsTable") as Table;

        this.variantBinding = this.importProblemModel.bindList("/variants") as JSONListBinding;
        this.variantBinding.attachChange(this.onVariantsChanged, this);
    }


    public async openDialog(importProblems: V4Context[], saveCallBack: () => void): Promise<void> {
        this.saveCallBack = saveCallBack;
        this.hasSaved = false;
        this.importProblemContexts = importProblems;
        this.dialogModel.setData({
            currentIndex: 0,
            total: importProblems.length,
        });
        await this.loadCurrentImportProblem();
        this.dialog.open();
    }

    public onClosePopup(): void {
        this.dialog.close();
    }

    private onAfterClose = (): void => {
        if (this.hasSaved) {
            this.hasSaved = false;
            this.saveCallBack?.();
        }
    };

    public async onSaveAndNext(): Promise<void> {
        const rememberSolution = this.viewModel.getProperty("/rememberSolution") as boolean;
        const currentIndex = this.dialogModel.getProperty("/currentIndex") as number;
        const total = this.dialogModel.getProperty("/total") as number;
        const importProblem = this.getCurrentImportProblem();
        const selectedVariant = this.importProblemModel.getProperty("/selectedVariant") as Product;

        const payload = {
            importProblemGuid: importProblem.Guid,
            productGuid: selectedVariant.Guid,
        };

        this.dialog.setBusy(true);
        try {
            if (rememberSolution) {
                await ApiService.getInstance().postRestJsonData("/import/problem/solution/create", payload);
            }
            await ApiService.getInstance().postRestJsonData("/import/problem/resolve", payload);
            this.hasSaved = true;

            const nextIndex = currentIndex + 1;
            if (nextIndex >= total) {
                this.dialog.close();
            } else {
                this.dialogModel.setProperty("/currentIndex", nextIndex);
                await this.loadCurrentImportProblem();
            }
        } finally {
            this.dialog.setBusy(false);
        }
    }

    public onArticleChanged(event: Field$ChangeEvent): void {
        if (!event.getParameter("valid")) {
            return;
        }
        this.resetSelectedVariant(["ArticleName", "ArticleNumber", "ArticleGuid"]);
        const oVH = this.getView().byId(event.getSource().getValueHelp()) as ValueHelp;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const articleContext = oVH.getControlDelegate().getSelectedContext(oVH) as V4Context;

        this.importProblemModel.setProperty("/MaterialGroupGuid", articleContext.getProperty("MaterialGroupGuid"));
        this.importProblemModel.setProperty("/MaterialGroupName", articleContext.getProperty("MaterialGroupName"));
        this.importProblemModel.setProperty("/selectedVariant/ArticleName", articleContext.getProperty("Name"));
        this.importProblemModel.setProperty("/selectedVariant/ArticleGuid", articleContext.getProperty("Guid"));
        this.importProblemModel.setProperty("/selectedVariant/ArticleNumber", articleContext.getProperty("Number"));

        (this.getModel() as ODataModel)
            .bindList("/Products", undefined, undefined, undefined, {
                $filter: `ArticleGuid eq '${event.getParameter("value")}'`,
            })
            .requestContexts(0, Infinity)
            .then((contexts) => {
                this.importProblemModel.setProperty("/variants", contexts.map((context) => context.getObject()));
            }, () => {
            });
    }

    public showSaveButton(...values: any[]): boolean {
        return values.every((value) => !!value);
    }

    public onVariantSelectionChange(event: any): void {
        if (event.getSource().getSelectedIndex() === -1) {
            return;
        }
        const variant = event.getParameter("rowContext").getObject();
        this.importProblemModel.setProperty("/selectedVariant", { ...variant });
    }

    public handleVariantTableChanged(event: any): void {
        this.viewModel.setProperty("/VariantsCount", event.getSource().getAllCurrentContexts().length);
    }

    public onSearchPossibleVariants(event: SearchField$SearchEvent): void {
        this.variantSearchQuery = event.getParameter("query") ?? "";
        this.applyVariantSearchFilter();
    }

    public async onOpenNewArticleDialog(): Promise<void> {
        const view = await ViewHandler.getView("vamos.cockpit.data.article.view.NewArticleDialog", this.getComponent());
        const controller = view.getController() as NewArticleDialogController;
        controller.openDialog(false, (article: Article) => {
            this.articleSaveDone(article);
        });
    }

    public async openNewVariantDialog(): Promise<void> {
        const view = await ViewHandler.getView("vamos.cockpit.data.article.view.NewVariantDialog", this.getComponent());
        const controller = view.getController() as NewVariantDialogController;
        const product = this.importProblemModel.getProperty("/selectedVariant") as Product;

        const path = this.getKeyPredicateFromEntitySet("Article", product.ArticleGuid);
        const model = this.getModel() as ODataModel;
        const contextBinding = model.bindContext(path);
        const article = await contextBinding.requestObject() as Article;

        await controller.openDialog(article, product, true, false, (savedProduct: Product) => {
            this.afterVariantSaved(savedProduct);
        });
    }

    private getCurrentImportProblem(): ImportProblem {
        const currentIndex = this.dialogModel.getProperty("/currentIndex") as number;
        return this.importProblemContexts[currentIndex].getObject() as ImportProblem;
    }

    private async loadCurrentImportProblem(): Promise<void> {
        const importProblem = { ...this.getCurrentImportProblem() };

        this.viewModel.setProperty("/rememberSolution", false);
        this.variantSearchQuery = "";
        (this.byId("possibleVariantsSearchField") as SearchField).setValue("");

        this.importProblemModel.setProperty("/MaterialGroupName", importProblem.MaterialGroupName || undefined);

        let filter: Filter;
        if (importProblem.ArticleGuid) {
            filter = new Filter("ArticleGuid", FilterOperator.EQ, importProblem.ArticleGuid);
        } else if (importProblem.ArticleNumber) {
            filter = new Filter({
                path: "ArticleNumber", operator: FilterOperator.EQ, value1: importProblem.ArticleNumber, caseSensitive: false,
            });
        } else {
            filter = new Filter("Guid", FilterOperator.EQ, null);
        }

        this.importProblemVariantsTable.setNoData(this.getText("NoDataFoundForArticle", [importProblem.ArticleNumber]));

        const measurementUnits = await this.measurementLoadedPromise;
        const unitContext = measurementUnits.find((context) => {
            return context.getProperty("SapCode") === (importProblem.UnitLength ? "02" : "01");
        });

        importProblem.UnitLength = importProblem.UnitLength || 1;
        if (unitContext) {
            importProblem.BaseUnitGuid = unitContext.getProperty("Guid");
            importProblem.BaseUnitName = unitContext.getProperty("Name");
        }
        this.importProblemModel.setProperty("/importedVariant", importProblem);
        this.importProblemModel.setProperty("/selectedVariant", {
            ArticleGuid: importProblem.ArticleGuid, ArticleName: importProblem.ArticleName, ArticleNumber: importProblem.ArticleNumber,
        });

        const contexts = await (this.getModel() as ODataModel)
            .bindList("/Products", undefined, undefined, filter, { $count: true })
            .requestContexts(0, 100000);
        this.importProblemModel.setProperty("/variants", contexts.map((context) => context.getObject()));
    }

    private onVariantsChanged = (event: any): void => {
        const importedVariant = this.importProblemModel.getProperty("/importedVariant");
        const currentProducts = event.getSource().getAllCurrentContexts().map((context: V4Context) => {
            const product = context.getObject();
            product.BaseAmountWUnit = `${product.BaseAmount} ${product.BaseUnitName}`;
            product.Matching = 0;
            const propertyFields = ["InnerLaminationGuid", "OuterLaminationGuid", "CarrierGuid", "SealColorGuid", "SealGuid", "GeometryGuid", "AdditionalEquipmentGuid"];
            propertyFields.forEach((propertyName) => {
                if (product[propertyName] === importedVariant?.[propertyName]) {
                    product.Matching++;
                }
            });
            return product;
        });

        this.importProblemModel.setProperty("/variants", currentProducts);
        if (event.getSource().getAllCurrentContexts().length === 1) {
            this.importProblemModel.setProperty("/selectedVariant", { ...currentProducts[0] });
        }
        this.viewModel.setProperty("/VariantsCount", currentProducts.length);
        const bindingInfo = this.importProblemVariantsTable.getBindingInfo("rows") as AggregationBindingInfo;
        this.importProblemVariantsTable.bindAggregation("rows", bindingInfo);
        this.applyVariantSearchFilter();
        // rebinding clears the selection, so select the single variant afterwards
        if (currentProducts.length === 1) {
            this.importProblemVariantsTable.setSelectedIndex(0);
        }
    };

    private applyVariantSearchFilter(): void {
        const searchFilters = this.variantSearchQuery
            ? ["ArticleNumber", "Name", "InnerLaminationName", "OuterLaminationName",
                "SealName", "SealColorName", "CarrierName", "GeometryName", "AdditionalEquipmentName"]
                .map((path) => new Filter({
                    path: path,
                    operator: FilterOperator.Contains,
                    value1: this.variantSearchQuery,
                    caseSensitive: false,
                }))
            : [];
        const binding = this.importProblemVariantsTable.getBinding("rows") as JSONListBinding;
        binding?.filter(searchFilters.length > 0 ? new Filter({ filters: searchFilters, and: false }) : [], "Application");
    }

    private resetSelectedVariant(excludedProperties: string[] = []): void {
        const oldVariant = this.importProblemModel.getProperty("/selectedVariant");
        const newVariant: any = {};
        excludedProperties.forEach((propertyName) => {
            newVariant[propertyName] = oldVariant[propertyName];
        });
        this.importProblemModel.setProperty("/selectedVariant", { ...newVariant });
    }

    private articleSaveDone(article: Article): void {
        void this.openNewVariantDialog();
        this.importProblemModel.setProperty("/MaterialGroupName", article.MaterialGroupName);
        this.importProblemModel.setProperty("/MaterialGroupGuid", article.MaterialGroupGuid);
        this.importProblemModel.setProperty("/selectedVariant/ArticleNumber", article.Number);
        this.importProblemModel.setProperty("/selectedVariant/ArticleName", article.Name);
        this.importProblemModel.setProperty("/selectedVariant/ArticleGuid", article.Guid);
    }

    private afterVariantSaved(product: Product): void {
        (this.getModel() as ODataModel)
            .bindList("/Products", undefined, undefined, undefined, {
                $filter: `ArticleGuid eq '${product.ArticleGuid}'`,
            })
            .requestContexts(0, Infinity)
            .then((contexts) => {
                this.importProblemModel.setProperty("/variants", contexts.map((context) => context.getObject()));
            }, () => {
            });
    }
}
